<template>
  <section class="notes-panel">
    <div class="panel-header">
      <div>
        <p class="panel-kicker">Notes</p>
        <h2>Quick notes</h2>
      </div>
    </div>

    <form
      v-if="editingIndex !== null"
      class="panel-form"
      @submit.prevent="handleSubmit"
    >
      <div class="field-group">
        <input v-model="form.title" type="text" placeholder="Title" required />
        <select v-model="form.color" aria-label="Note color">
          <option value="yellow">Yellow</option>
          <option value="blue">Blue</option>
          <option value="green">Green</option>
          <option value="pink">Pink</option>
        </select>
      </div>
      <textarea
        v-model="form.content"
        rows="3"
        placeholder="Write a note"
        required
      ></textarea>
      <div class="form-actions">
        <button type="button" class="btn-ghost" @click="resetForm">
          Cancel
        </button>
        <button type="submit" class="btn-primary">
          {{ editingIndex === null ? "Save" : "Update" }}
        </button>
      </div>
    </form>

    <div v-if="dashboardStore.isLoadingNotes" class="panel-empty">
      Loading notes...
    </div>
    <div v-else-if="dashboardStore.notes.length === 0" class="panel-empty">
      No notes yet.
    </div>

    <div class="notes-grid">
      <article
        v-for="(note, index) in dashboardStore.notes"
        :key="note.id || `${note.title}-${index}`"
        class="note-card"
        :data-color="note.color"
        @click="startEdit(index)"
      >
        <header>
          <h3>{{ note.title }}</h3>
          <button
            type="button"
            class="icon-btn"
            @click.stop="removeNote(index)"
          >
            ×
          </button>
        </header>
        <p>{{ note.content }}</p>
      </article>
    </div>

    <template v-if="dashboardStore.isSchedulesVisible">
      <div class="panel-header schedule-header">
        <div>
          <p class="panel-kicker">Schedule</p>
          <h2>Upcoming</h2>
        </div>
      </div>

      <form
        v-if="editingScheduleIndex !== null"
        class="panel-form"
        @submit.prevent="handleScheduleSubmit"
      >
        <input
          v-model="scheduleForm.title"
          type="text"
          placeholder="Title"
          required
        />
        <input
          v-model="scheduleForm.datetime"
          type="text"
          placeholder="日時（例: 明日15時）"
        />
        <div class="form-actions">
          <button type="button" class="btn-ghost" @click="resetScheduleForm">
            Cancel
          </button>
          <button type="submit" class="btn-primary">Update</button>
        </div>
      </form>

      <div v-if="dashboardStore.isLoadingSchedules" class="panel-empty">
        Loading schedules...
      </div>
      <div
        v-else-if="dashboardStore.schedules.length === 0"
        class="panel-empty"
      >
        No schedules yet.
      </div>

      <ul class="schedule-list">
        <li
          v-for="(schedule, index) in dashboardStore.schedules"
          :key="schedule.id || `${schedule.title}-${index}`"
          class="schedule-item"
          @click="startEditSchedule(index)"
        >
          <div class="schedule-main">
            <div class="schedule-title">{{ schedule.title }}</div>
            <div class="schedule-time">{{ formatScheduleRange(schedule) }}</div>
          </div>
          <button
            type="button"
            class="icon-btn"
            @click.stop="removeSchedule(index)"
          >
            ×
          </button>
        </li>
      </ul>
    </template>
  </section>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted } from "vue";
import { useDashboardStore } from "@/stores/dashboard";
import type { Schedule } from "@/types/models";

const dashboardStore = useDashboardStore();
const editingIndex = ref<number | null>(null);
const editingScheduleIndex = ref<number | null>(null);

const form = reactive({
  title: "",
  content: "",
  color: "yellow",
});

const scheduleForm = reactive({
  title: "",
  datetime: "",
});

onMounted(() => {
  dashboardStore.fetchNotes();
  dashboardStore.fetchSchedules();
});

const resetForm = (): void => {
  form.title = "";
  form.content = "";
  form.color = "yellow";
  editingIndex.value = null;
};

const resetScheduleForm = (): void => {
  scheduleForm.title = "";
  scheduleForm.datetime = "";
  editingScheduleIndex.value = null;
};

const handleSubmit = (): void => {
  if (!form.title.trim() || !form.content.trim()) return;
  const payload = {
    title: form.title.trim(),
    content: form.content.trim(),
    color: form.color,
  };
  if (editingIndex.value === null) return;
  dashboardStore.updateNote(editingIndex.value, payload);
  resetForm();
};

const startEdit = (index: number): void => {
  const note = dashboardStore.notes[index];
  if (!note) return;
  form.title = note.title;
  form.content = note.content;
  form.color = note.color;
  editingIndex.value = index;
};

const removeNote = (index: number): void => {
  dashboardStore.removeNote(index);
};

const handleScheduleSubmit = (): void => {
  if (!scheduleForm.title.trim()) return;
  if (editingScheduleIndex.value === null) return;
  dashboardStore.updateSchedule(editingScheduleIndex.value, {
    title: scheduleForm.title.trim(),
    datetime: scheduleForm.datetime.trim() || null,
  });
  resetScheduleForm();
};

const startEditSchedule = (index: number): void => {
  const schedule = dashboardStore.schedules[index];
  if (!schedule) return;
  scheduleForm.title = schedule.title;
  scheduleForm.datetime = formatScheduleEditValue(schedule);
  editingScheduleIndex.value = index;
};

const removeSchedule = (index: number): void => {
  dashboardStore.removeSchedule(index);
};

const formatScheduleRange = (schedule: Schedule): string => {
  const start = new Date(schedule.startAt);
  const end = schedule.endAt ? new Date(schedule.endAt) : null;
  if (Number.isNaN(start.getTime())) return "";
  const dateLabel = start.toLocaleDateString("ja-JP", {
    month: "short",
    day: "numeric",
  });
  const startTime = start.toLocaleTimeString("ja-JP", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (end && !Number.isNaN(end.getTime())) {
    const endTime = end.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateLabel} ${startTime} - ${endTime}`;
  }
  return `${dateLabel} ${startTime}`;
};

const formatScheduleEditValue = (schedule: Schedule): string => {
  const start = new Date(schedule.startAt);
  const end = schedule.endAt ? new Date(schedule.endAt) : null;
  if (Number.isNaN(start.getTime())) return "";
  const dateLabel = start.toLocaleDateString("ja-JP", {
    month: "numeric",
    day: "numeric",
  });
  const startTime = start.toLocaleTimeString("ja-JP", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (end && !Number.isNaN(end.getTime())) {
    const endTime = end.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateLabel} ${startTime}-${endTime}`;
  }
  return `${dateLabel} ${startTime}`;
};
</script>

<style scoped>
.notes-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.panel-kicker {
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7a7a7a;
}

.panel-header h2 {
  margin: 4px 0 0 0;
  font-size: 18px;
  font-weight: 600;
  color: #222;
}

.panel-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #ececec;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
}

.panel-form input,
.panel-form textarea,
.panel-form select {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e3e3e3;
  font-size: 12px;
  background: #fafafa;
  color: #222;
  transition:
    border 0.2s ease,
    box-shadow 0.2s ease;
}

.panel-form input:focus,
.panel-form textarea:focus,
.panel-form select:focus {
  outline: none;
  border-color: #1f73e6;
  box-shadow: 0 0 0 3px rgba(31, 115, 230, 0.15);
}

.field-group {
  display: grid;
  grid-template-columns: 1fr 120px;
  gap: 10px;
}

.form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-primary,
.btn-ghost {
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #1f73e6;
  color: white;
}

.btn-primary:hover {
  background: #1557b0;
}

.btn-ghost {
  background: transparent;
  color: #333;
  border-color: #e0e0e0;
}

.btn-ghost:hover {
  background: #f6f6f6;
}

.panel-empty {
  color: #777;
  font-size: 12px;
}

.notes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.schedule-header {
  margin-top: 8px;
}

.schedule-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.schedule-item {
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 12px 14px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
}

.schedule-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.schedule-title {
  font-size: 13px;
  font-weight: 600;
  color: #222;
}

.schedule-time {
  font-size: 12px;
  color: #666;
}

.note-card {
  padding: 14px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fffdf5;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

.note-card[data-color="blue"] {
  background: #f3f8ff;
}

.note-card[data-color="green"] {
  background: #f3fbf6;
}

.note-card[data-color="pink"] {
  background: #fff4f7;
}

.note-card header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.note-card h3 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #222;
}

.note-card p {
  margin: 0;
  font-size: 12px;
  color: #4b4b4b;
  white-space: pre-wrap;
}

.icon-btn {
  border: none;
  background: transparent;
  font-size: 16px;
  cursor: pointer;
  color: #666;
}

.icon-btn:hover {
  color: #111;
}
</style>
